import { CardComponent } from "@/pages-components/quotes/widgets/statistics-section/card";
import { useStyle } from "./style";
import { useTranslation } from "react-i18next";
import { useCardWidget } from "./use-cards-widget";
import { useRecoilValue } from "recoil";
import { homeReportsState } from "@/pages-components/quote-new/store/quote";
import { useTheme } from "@/contexts/theme/useTheme";
import { EStatisticsLabels } from "@/pages-components/quotes/enums";
import { useQuoteGetData } from "@/pages-components/quote-new/use-quote-get-data";
import { systemCurrencyState } from "@/store";

// Cards whose value is a monetary amount and should be shown with the currency
// unit, exactly like the "Total price" column on the orders page.
const MONEY_CARD_KEYS: string[] = [EStatisticsLabels.ORDERS_TOTAL];

const PERCENTAGE_CARD_KEYS: string[] = [
  EStatisticsLabels.SUCCESS_RATE,
  EStatisticsLabels.TARGET_PERCENTAGE,
  EStatisticsLabels.MONTHLY_PERFORMANCE,
];

const CardsWidget = () => {
  const { getComponentOverride } = useTheme();

  const OverrideComponent = getComponentOverride("CardsWidget");

  // If override exists (e.g., when hp-client theme is active), use it
  if (OverrideComponent) {
    return <OverrideComponent />;
  }

  // Otherwise, render the default CardsWidget layout
  return <DefaultCardsWidget />;
};

// Separate component for default layout to keep hooks unconditional
const DefaultCardsWidget = () => {
  const { classes } = useStyle();
  const { t } = useTranslation();
  const allReports = useRecoilValue<any>(homeReportsState);
  const {
    getCardIcon,
    getCardColor,
    getCardTextColor,
    getCardBorder,
    getCardLabel,
  } = useCardWidget();

  // Show the currency unit to the right of monetary card values.
  const { getCurrencyUnitText } = useQuoteGetData();
  const systemCurrency = useRecoilValue<any>(systemCurrencyState);
  const currUnit = getCurrencyUnitText(systemCurrency);

  const formatCardNumber = (card: any) => {
    const value = card?.value;
    if (MONEY_CARD_KEYS.includes(card?.key)) {
      // Currency unit shown to the right of the amount. Non-breaking space keeps
      // them on the same line (otherwise it wraps in the narrow card).
      return `${value}\u00A0${currUnit}`;
    }
    return value;
  };

  const percentageCards =
    allReports?.filter((card) =>
      PERCENTAGE_CARD_KEYS.includes(card?.key)
    ) || [];
  const valueCards =
    allReports?.filter(
      (card) =>
        !PERCENTAGE_CARD_KEYS.includes(card?.key) &&
        typeof card?.value !== "object" &&
        card?.value !== null &&
        card?.value !== undefined
    ) || [];

  // Stack value cards vertically and let them overflow into a second column.
  const valueRows = Math.max(1, Math.ceil(valueCards.length / 2));

  return (
    <div style={classes.mainContainer}>
      <div style={classes.firstDiv}>
        {percentageCards.map((card) => (
          <CardComponent
            key={card?.key}
            style={{
              ...classes.firstCard,
              background: getCardColor(card?.key),
              border: getCardBorder(card?.key),
              boxShadow:
                "var(--theme-card-shadow, rgba(0, 0, 0, 0.2) 0px 2px 1px -1px, rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, rgba(0, 0, 0, 0.12) 0px 1px 3px 0px)",
              borderRadius: "var(--theme-card-radius, 4px)",
            }}
            text={t(getCardLabel(card?.key))}
            number={!isNaN(card.value) ? `${card.value}%` : "0%"}
            textColor={getCardTextColor(card?.key)}
            icon={getCardIcon(card?.key)}
          />
        ))}
      </div>
      <div
        style={{
          ...classes.secondDiv,
          gridTemplateRows: `repeat(${valueRows}, minmax(0, 1fr))`,
        }}
      >
        {valueCards.map((card) => {
          return (
            <CardComponent
              key={card?.key}
              style={{
                ...classes.secondCard,
                background: getCardColor(card?.key),
                border: getCardBorder(card?.key),
                boxShadow:
                  "var(--theme-card-shadow, rgba(0, 0, 0, 0.2) 0px 2px 1px -1px, rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, rgba(0, 0, 0, 0.12) 0px 1px 3px 0px)",
                borderRadius: "var(--theme-card-radius, 4px)",
              }}
              text={t(getCardLabel(card?.key))}
              number={formatCardNumber(card)}
              textColor={getCardTextColor(card?.key)}
              icon={getCardIcon(card?.key)}
            />
          )
        })}
      </div>
    </div>
  );
};

export { CardsWidget };
